import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment, { type Moment } from 'moment-timezone';
import PageMeta from '../../components/SEO/PageMeta';
import styles from './TimeToolsPage.module.css';

const DEFAULT_TIME_ZONE = 'UTC';
const SAMPLE_START_TIME = '2026-06-13 09:00';
const SAMPLE_END_TIME = '2026-06-13 17:30';
const INITIAL_INTERVALS: TimeInterval[] = [
  { id: 1, startTime: SAMPLE_START_TIME, endTime: SAMPLE_END_TIME },
];
const TIME_ZONE_OPTIONS: TimeZoneOption[] = [
  { label: 'UTC', offsetMinutes: 0 },
  { label: 'GMT (+00:00)', offsetMinutes: 0 },
  { label: 'EST (-05:00)', offsetMinutes: -5 * 60 },
  { label: 'EDT (-04:00)', offsetMinutes: -4 * 60 },
  { label: 'CST (-06:00)', offsetMinutes: -6 * 60 },
  { label: 'CDT (-05:00)', offsetMinutes: -5 * 60 },
  { label: 'MST (-07:00)', offsetMinutes: -7 * 60 },
  { label: 'MDT (-06:00)', offsetMinutes: -6 * 60 },
  { label: 'PST (-08:00)', offsetMinutes: -8 * 60 },
  { label: 'PDT (-07:00)', offsetMinutes: -7 * 60 },
  { label: 'CET (+01:00)', offsetMinutes: 60 },
  { label: 'CEST (+02:00)', offsetMinutes: 2 * 60 },
  { label: 'EET (+02:00)', offsetMinutes: 2 * 60 },
  { label: 'EEST (+03:00)', offsetMinutes: 3 * 60 },
  { label: 'MSK (+03:00)', offsetMinutes: 3 * 60 },
  { label: 'GST (+04:00)', offsetMinutes: 4 * 60 },
  { label: 'PKT (+05:00)', offsetMinutes: 5 * 60 },
  { label: 'IST (+05:30)', offsetMinutes: 5 * 60 + 30 },
  { label: 'NPT (+05:45)', offsetMinutes: 5 * 60 + 45 },
  { label: 'BST (+06:00)', offsetMinutes: 6 * 60 },
  { label: 'ICT (+07:00)', offsetMinutes: 7 * 60 },
  { label: 'SGT (+08:00)', offsetMinutes: 8 * 60 },
  { label: 'CST (+08:00)', offsetMinutes: 8 * 60 },
  { label: 'JST (+09:00)', offsetMinutes: 9 * 60 },
  { label: 'KST (+09:00)', offsetMinutes: 9 * 60 },
  { label: 'AEST (+10:00)', offsetMinutes: 10 * 60 },
  { label: 'AEDT (+11:00)', offsetMinutes: 11 * 60 },
  { label: 'NZST (+12:00)', offsetMinutes: 12 * 60 },
  { label: 'NZDT (+13:00)', offsetMinutes: 13 * 60 },
];

const PARSE_FORMATS = [
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD',
  'HH:mm:ss',
  'HH:mm',
  moment.ISO_8601,
];

interface ParsedTime {
  normalizedInput: string;
  moment: Moment;
}

interface DurationBreakdown {
  milliseconds: number;
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  weeks: number;
  clock: string;
}

interface TimeZoneOption {
  label: string;
  offsetMinutes: number;
}

interface TimeInterval {
  id: number;
  startTime: string;
  endTime: string;
}

interface IntervalResult {
  interval: TimeInterval;
  first: ParsedTime | null;
  second: ParsedTime | null;
  duration: DurationBreakdown | null;
  signedMilliseconds: number | null;
}

export default function TimeToolsPage() {
  const [timeZone, setTimeZone] = useState(DEFAULT_TIME_ZONE);
  const [startTime, setStartTime] = useState(SAMPLE_START_TIME);
  const [endTime, setEndTime] = useState(SAMPLE_END_TIME);
  const [intervals, setIntervals] = useState<TimeInterval[]>(INITIAL_INTERVALS);
  const [currentTimeTick, setCurrentTimeTick] = useState(() => Date.now());

  const selectedOffset = useMemo(() => parseUtcOffset(timeZone), [timeZone]);
  const currentTime = useMemo(
    () => moment(currentTimeTick).utcOffset(selectedOffset).format('DD/MM/YYYY => hh:mm:ss A'),
    [currentTimeTick, selectedOffset],
  );
  const comparison = useMemo(() => {
    const first = parseTimeInput(startTime, selectedOffset);
    const second = parseTimeInput(endTime, selectedOffset);

    if (!first || !second) {
      return { first, second, duration: null, signedMilliseconds: null };
    }

    const signedMilliseconds = second.moment.valueOf() - first.moment.valueOf();
    return {
      first,
      second,
      duration: getDurationBreakdown(Math.abs(signedMilliseconds)),
      signedMilliseconds,
    };
  }, [endTime, selectedOffset, startTime]);
  const intervalResults = useMemo(
    () =>
      intervals.map((interval): IntervalResult => {
        const first = parseTimeInput(interval.startTime, selectedOffset);
        const second = parseTimeInput(interval.endTime, selectedOffset);

        if (!first || !second) {
          return { interval, first, second, duration: null, signedMilliseconds: null };
        }

        const signedMilliseconds = second.moment.valueOf() - first.moment.valueOf();
        return {
          interval,
          first,
          second,
          duration: getDurationBreakdown(Math.abs(signedMilliseconds)),
          signedMilliseconds,
        };
      }),
    [intervals, selectedOffset],
  );
  const validIntervalResults = intervalResults.filter((result) => result.duration);
  const totalDuration = getDurationBreakdown(
    validIntervalResults.reduce((total, result) => total + (result.duration?.milliseconds ?? 0), 0),
  );
  const directionLabel =
    comparison.signedMilliseconds === null
      ? 'Enter two valid times'
      : comparison.signedMilliseconds === 0
        ? 'Both times are equal'
        : comparison.signedMilliseconds > 0
          ? 'Second time is after the first'
          : 'Second time is before the first';

  useEffect(() => {
    const intervalId = window.setInterval(() => setCurrentTimeTick(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  function updateInterval(id: number, field: 'startTime' | 'endTime', value: string) {
    setIntervals((current) =>
      current.map((interval) => (interval.id === id ? { ...interval, [field]: value } : interval)),
    );
  }

  function addInterval() {
    setIntervals((current) => [
      ...current,
      {
        id: Date.now(),
        startTime: '',
        endTime: '',
      },
    ]);
  }

  function removeInterval(id: number) {
    setIntervals((current) =>
      current.length === 1 ? current : current.filter((interval) => interval.id !== id),
    );
  }

  function resetIntervals() {
    setIntervals(INITIAL_INTERVALS);
  }

  return (
    <>
      <PageMeta
        title="Free Online Time Difference Calculator"
        description="Compare two free-form times in UTC or another timezone and instantly see total seconds, minutes, hours, days, and weeks."
        path="/time-tools"
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        <span>Time Tools</span>
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Timezone-aware calculator</p>
        <h1>Time Difference Calculator</h1>
        <p>
          Select a timezone, enter two times, and compare the exact duration between them as total
          seconds, minutes, hours, days, and weeks. UTC is selected by default.
        </p>
      </section>

      <section className={styles.toolShell} aria-labelledby="time-tool-heading">
        <div className={styles.toolHeader}>
          <div>
            <p className={styles.eyebrow}>Compare two times</p>
            <h2 id="time-tool-heading">Duration output</h2>
            <p>
              Use formats like <code>09:30</code>, <code>18:45:15</code>,{' '}
              <code>2026-06-13 09:30</code>, or ISO datetime strings.
            </p>
          </div>
          <label className={styles.timeZonePicker} htmlFor="time-zone">
            Timezone
            <select
              id="time-zone"
              value={timeZone}
              onChange={(event) => setTimeZone(event.target.value)}
            >
              {TIME_ZONE_OPTIONS.map((zone) => (
                <option key={zone.label} value={zone.label}>
                  {zone.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <section className={styles.currentTimeCard} aria-live="polite">
          <span>Current time in {timeZone}</span>
          <strong>{currentTime}</strong>
        </section>

        <div className={styles.inputGrid}>
          <label>
            First time
            <input
              type="text"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
              placeholder="2026-06-13 09:00"
              spellCheck={false}
            />
          </label>
          <label>
            Second time
            <input
              type="text"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
              placeholder="2026-06-13 17:30"
              spellCheck={false}
            />
          </label>
        </div>

        <div className={styles.resultPanel} aria-live="polite">
          <div className={styles.resultSummary}>
            <span>{directionLabel}</span>
            {comparison.duration ? (
              <strong>{comparison.duration.clock}</strong>
            ) : (
              <strong>--</strong>
            )}
          </div>

          {comparison.duration ? (
            <>
              <dl className={styles.statsGrid}>
                <DurationStat label="Total seconds" value={comparison.duration.seconds} />
                <DurationStat label="Total minutes" value={comparison.duration.minutes} />
                <DurationStat label="Total hours" value={comparison.duration.hours} />
                <DurationStat label="Total days" value={comparison.duration.days} />
                <DurationStat label="Total weeks" value={comparison.duration.weeks} />
                <DurationStat label="Milliseconds" value={comparison.duration.milliseconds} />
              </dl>
              <div className={styles.parsedGrid}>
                <ParsedTimeCard
                  label="First parsed time"
                  parsedTime={comparison.first}
                  offsetMinutes={selectedOffset}
                />
                <ParsedTimeCard
                  label="Second parsed time"
                  parsedTime={comparison.second}
                  offsetMinutes={selectedOffset}
                />
              </div>
            </>
          ) : (
            <p className={styles.errorText}>
              Please enter both times in a recognizable format. Time-only values are compared on
              1970-01-01 in the selected timezone.
            </p>
          )}
        </div>
      </section>

      <section className={styles.secondaryToolShell} aria-labelledby="multi-interval-heading">
        <div className={styles.intervalToolbar}>
          <div>
            <p className={styles.eyebrow}>Secondary feature</p>
            <h2 id="multi-interval-heading">Multiple interval total</h2>
            <p>Add time pairs and total their combined duration.</p>
          </div>
          <div className={styles.intervalActions}>
            <button type="button" className={styles.secondaryButton} onClick={resetIntervals}>
              Reset
            </button>
          </div>
        </div>

        <div className={styles.intervalList}>
          {intervalResults.map((result, index) => (
            <article key={result.interval.id} className={styles.intervalCard}>
              <div className={styles.intervalHeader}>
                <h3>Interval {index + 1}</h3>
                {intervals.length > 1 && (
                  <button
                    type="button"
                    className={styles.textButton}
                    onClick={() => removeInterval(result.interval.id)}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className={styles.inputGrid}>
                <label>
                  First time
                  <input
                    type="text"
                    value={result.interval.startTime}
                    onChange={(event) =>
                      updateInterval(result.interval.id, 'startTime', event.target.value)
                    }
                    placeholder="2026-06-13 09:00"
                    spellCheck={false}
                  />
                </label>
                <label>
                  Second time
                  <input
                    type="text"
                    value={result.interval.endTime}
                    onChange={(event) =>
                      updateInterval(result.interval.id, 'endTime', event.target.value)
                    }
                    placeholder="2026-06-13 17:30"
                    spellCheck={false}
                  />
                </label>
              </div>

              <div className={styles.intervalResult}>
                <span>{getIntervalDirectionLabel(result.signedMilliseconds)}</span>
                <strong>{result.duration ? result.duration.clock : '--'}</strong>
              </div>
            </article>
          ))}
        </div>

        <div className={styles.addIntervalFooter}>
          <button type="button" className={styles.primaryButton} onClick={addInterval}>
            Add New
          </button>
        </div>

        <div className={styles.resultPanel} aria-live="polite">
          <div className={styles.resultSummary}>
            <span>Total from {validIntervalResults.length} valid interval(s)</span>
            <strong>{totalDuration.clock}</strong>
          </div>

          {validIntervalResults.length > 0 ? (
            <>
              <dl className={styles.statsGrid}>
                <DurationStat label="Total seconds" value={totalDuration.seconds} />
                <DurationStat label="Total minutes" value={totalDuration.minutes} />
                <DurationStat label="Total hours" value={totalDuration.hours} />
                <DurationStat label="Total days" value={totalDuration.days} />
                <DurationStat label="Total weeks" value={totalDuration.weeks} />
                <DurationStat label="Milliseconds" value={totalDuration.milliseconds} />
              </dl>
            </>
          ) : (
            <p className={styles.errorText}>
              Add at least one valid first and second time. Time-only values are compared on
              1970-01-01 in the selected timezone offset.
            </p>
          )}
        </div>
      </section>
    </>
  );
}

function DurationStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{formatNumber(value)}</dd>
    </div>
  );
}

function ParsedTimeCard({
  label,
  parsedTime,
  offsetMinutes,
}: {
  label: string;
  parsedTime: ParsedTime | null;
  offsetMinutes: number;
}) {
  return (
    <article className={styles.parsedCard}>
      <h3>{label}</h3>
      <p>{parsedTime ? parsedTime.normalizedInput : 'Invalid time'}</p>
      <output>
        {parsedTime
          ? parsedTime.moment.utcOffset(offsetMinutes).format('YYYY-MM-DD HH:mm:ss [UTC]Z')
          : '--'}
      </output>
      <small>{parsedTime ? parsedTime.moment.toISOString() : ''}</small>
    </article>
  );
}

function parseTimeInput(input: string, offsetMinutes: number): ParsedTime | null {
  const normalizedInput = input.trim();
  if (!normalizedInput) {
    return null;
  }

  const timeOnly = normalizedInput.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  const valueToParse = timeOnly ? `1970-01-01 ${normalizedInput}` : normalizedInput;

  const parsed = moment(valueToParse, PARSE_FORMATS, true);
  if (!parsed.isValid()) {
    const fallback = moment(normalizedInput);
    if (!fallback.isValid()) {
      return null;
    }
    return { normalizedInput, moment: fallback.utcOffset(offsetMinutes, true) };
  }

  return { normalizedInput, moment: parsed.utcOffset(offsetMinutes, true) };
}

function parseUtcOffset(label: string): number {
  return TIME_ZONE_OPTIONS.find((option) => option.label === label)?.offsetMinutes ?? 0;
}

function getIntervalDirectionLabel(signedMilliseconds: number | null): string {
  if (signedMilliseconds === null) {
    return 'Enter valid start and end times';
  }

  if (signedMilliseconds === 0) {
    return 'No time difference';
  }

  return signedMilliseconds > 0 ? 'Talk interval counted' : 'End is before start, counted absolute';
}

function getDurationBreakdown(milliseconds: number): DurationBreakdown {
  const duration = moment.duration(milliseconds);
  const totalSeconds = duration.asSeconds();
  const totalMinutes = duration.asMinutes();
  const totalHours = duration.asHours();
  const days = duration.asDays();
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  return {
    milliseconds,
    seconds: totalSeconds,
    minutes: totalMinutes,
    hours: totalHours,
    days,
    weeks: days / 7,
    clock: `${hours}h ${minutes}m ${seconds}s`,
  };
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 4,
  }).format(value);
}
